import type {ApiKeySummary} from "../../../store/features/integrations/integrationsService";

const ENABLED_STATUS = 'ENABLED' as ApiKeySummary['status'];

export type ApiTriggerTileState = 'off' | 'needs-key' | 'instructions';

export const getApiTriggerTileState = (
    apiTriggerEnabled: boolean,
    keys: readonly ApiKeySummary[],
): ApiTriggerTileState => {
    if (!apiTriggerEnabled) return 'off';
    return keys.some((key) => key.status === ENABLED_STATUS) ? 'instructions' : 'needs-key';
};

export const suggestedApiKeyName = (system: string, environment: string): string =>
    ['ENS', system.trim(), environment.trim()].filter(Boolean).join(' / ');

export const orderApiKeys = (
    keys: readonly ApiKeySummary[],
    system: string,
    environment: string,
): ApiKeySummary[] => {
    const tokens = [system, environment].map((value) => value.trim().toLowerCase()).filter(Boolean);
    const score = (key: ApiKeySummary) => {
        const name = key.name.toLowerCase();
        const matchCount = tokens.filter((token) => name.includes(token)).length;
        return (key.status === ENABLED_STATUS ? 100 : 0) + matchCount;
    };
    return [...keys].sort((left, right) => score(right) - score(left) || left.name.localeCompare(right.name));
};

export const normalizeApiBaseUrl = (value: string): string => value.replace(/\/+$/, '');

export const inferEnvironmentLabel = (baseUrl: string): string => {
    const host = baseUrl.toLowerCase();
    if (host.includes('backend-3-api.phonehq.com')) return 'production';
    if (host.includes('phq-preprod.click')) return 'pre-production';
    if (host.includes('dev-')) return 'development';
    return 'custom';
};

const shellSingleQuote = (value: string): string => `'${value.replace(/'/g, `'"'"'`)}'`;

export const buildCurlExample = ({
    baseUrl,
    tenant,
    templateId,
}: {
    baseUrl: string;
    tenant: string;
    templateId: number;
}): string => `export PHQ_BASE_URL=${shellSingleQuote(normalizeApiBaseUrl(baseUrl))}
export PHQ_API_CLIENT=${shellSingleQuote(tenant)}
export PHQ_API_KEY='<read-from-secret-manager>'

curl --fail-with-body \\
  --request POST \\
  "\${PHQ_BASE_URL}/api/v1/ens/alerts/trigger" \\
  --header 'Content-Type: application/json' \\
  --header "X-API-Client: \${PHQ_API_CLIENT}" \\
  --header "X-API-Key: \${PHQ_API_KEY}" \\
  --data '${JSON.stringify({
    templateId,
    dedupKey: 'monitoring:incident-456',
    additionalMessage: 'Detected by the boiler-room monitor',
}, null, 2)}'`;
